import Scene              from './Scene'
import * as THREE         from 'three'
import FBXLoader          from '../vendor/three-loaders/FBXLoader'
import TWEEN              from '@tweenjs/tween.js'
import classifyPoint      from 'robust-point-in-polygon'
import * as rasterizeHTML from 'rasterizehtml'
import GLTFLoader         from '../vendor/three-loaders/GLTFLoader'
import Sky                from '../vendor/three-objects/Sky'
import * as dat           from 'dat.gui'

class Scene3D extends Scene {

  constructor(props) {
    super(props)
    this.interactiveObjects = []
    this.doorAnimation = { enter: {}, leave: {} }
    this.skyParameters = null
  }

  init() {
    //this.scene.background = new THREE.Color('#a0a0a0')
    //this.scene.fog = new THREE.Fog('#a0a0a0', 200, 1000)
    this.moveSpeed = 0
    this.moveDirection = 1
    this.unprocessedMove = false
    this.moveByClickRequest = false

    this.floorPlane = null
    this.techScene = new THREE.Scene()
    this.cubeCams = []

    super.init()

    this.initExhibitPoints()

    this.renderer.localClippingEnabled = true // to cut out wall behind the door
    this.renderer.sortObjects = false // this is a workaround for transparent door get bugged with transparent door-glow

    if (window.Config.scenes.developHelpers) {
      this.initGrid()
    }

    this.initFramesAnimations()

    if (window.Config.options.debug.debugGui) {
      this.initDebugGui()
    }
  }

  initDebugGui() {
    if (this.debugGui) {
      return
    }

    window.THREE = THREE

    this.debugGui = new dat.GUI()

    let debugSelectedData = {
      name: '',
      position: { x: 0.0, y: 0.0, z: 0.0 },
      positionPrecise: { x: 0.0, y: 0.0, z: 0.0 },
      rotation: { x: 0.0, y: 0.0, z: 0.0 }
    }

    let transformChange = () => {
      if (!this.debugSelectedObject) {
        return
      }

      this.debugSelectedObject.position.set(
        debugSelectedData.position.x + debugSelectedData.positionPrecise.x,
        debugSelectedData.position.y + debugSelectedData.positionPrecise.y,
        debugSelectedData.position.z + debugSelectedData.positionPrecise.z,
      )

      this.debugSelectedObject.rotation.set(
        debugSelectedData.rotation.x,
        debugSelectedData.rotation.y,
        debugSelectedData.rotation.z,
      )
    }

    let transformFolder = this.debugGui.addFolder('Transform')
    transformFolder.add(debugSelectedData, 'name').listen()

    let position = transformFolder.addFolder('Position')
    position.add(debugSelectedData.position, 'x').step(1).onChange(transformChange).listen()
    position.add(debugSelectedData.position, 'y').step(1).onChange(transformChange).listen()
    position.add(debugSelectedData.position, 'z').step(1).onChange(transformChange).listen()
    position.open()

    let positionPrecise = transformFolder.addFolder('Position Precise')
    positionPrecise.add(debugSelectedData.positionPrecise, 'x', 0.0, 1.0).step(0.1).onChange(transformChange).listen()
    positionPrecise.add(debugSelectedData.positionPrecise, 'y', 0.0, 1.0).step(0.1).onChange(transformChange).listen()
    positionPrecise.add(debugSelectedData.positionPrecise, 'z', 0.0, 1.0).step(0.1).onChange(transformChange).listen()

    let rotation = transformFolder.addFolder('Rotation')
    rotation.add(debugSelectedData.rotation, 'x', -Math.PI, Math.PI).step(0.001).onChange(transformChange).listen()
    rotation.add(debugSelectedData.rotation, 'y', -Math.PI, Math.PI).step(0.001).onChange(transformChange).listen()
    rotation.add(debugSelectedData.rotation, 'z', -Math.PI, Math.PI).step(0.001).onChange(transformChange).listen()
    rotation.open()

    this.debugSelectedObject = null
    this.setDebugSelectedObject = (object) => {
      console.log('object', object)
      if (object && object.children && object.children[0] && object.children[0].material && object.children[0].material[0]) {
        console.log('object.children[0].material[0]', object.children[0].material[0])
      }

      if (!object && this.debugSelectedObject) {
        this.outlineObject(this.debugSelectedObject, false)
      }


      this.debugSelectedObject = object
      if (!object) {
        transformFolder.name = 'Transform: none'
        debugSelectedData.name = ''
        debugSelectedData.position.x = 0.0
        debugSelectedData.position.y = 0.0
        debugSelectedData.position.z = 0.0
        debugSelectedData.positionPrecise.x = 0.0
        debugSelectedData.positionPrecise.y = 0.0
        debugSelectedData.positionPrecise.z = 0.0
        debugSelectedData.rotation.x = 0.0
        debugSelectedData.rotation.y = 0.0
        debugSelectedData.rotation.z = 0.0
        return
      }

      window.temp = object

      transformFolder.name = 'Transform: ' + object.name

      debugSelectedData.name = object.name

      debugSelectedData.position.x = Math.floor(object.position.x)
      debugSelectedData.position.y = Math.floor(object.position.y)
      debugSelectedData.position.z = Math.floor(object.position.z)

      debugSelectedData.positionPrecise.x = object.position.x - debugSelectedData.position.x
      debugSelectedData.positionPrecise.y = object.position.y - debugSelectedData.position.y
      debugSelectedData.positionPrecise.z = object.position.z - debugSelectedData.position.z

      debugSelectedData.rotation.x = object.rotation.x
      debugSelectedData.rotation.y = object.rotation.y
      debugSelectedData.rotation.z = object.rotation.z

      this.outlineObject(this.debugSelectedObject, true)
    }

    let ambientLight = this.scene.getObjectByName('ambient')
    if (ambientLight) {
      let folder = this.debugGui.addFolder('Ambient')
      let hexColor = ambientLight.color.getHex()
      let params = {
        color: hexColor
      }
      folder.addColor(params, 'color').onChange(() => {
        ambientLight.color.setHex(params.color)
      })
      folder.add(ambientLight, 'intensity', 0.0, 1.0, 0.001).listen()
    }

    let folder = this.debugGui.addFolder('Debug')
    folder.add(window.Config.options.debug, 'debugLights').onChange((value) => {
      let lights = []
      this.scene.traverse(child => {
        if (
          child instanceof THREE.PointLight
          || child instanceof THREE.DirectionalLight
          || child instanceof THREE.SpotLight
        ) {
          lights.push(child)
        }
      })
      lights.forEach(child => this.showLightHelper(child, value))
    })
    folder.add(window.Config.options.debug, 'ignoreCollisions')

    folder.add(window.Config.options.debug, 'showCollisionsBoundaries').onChange((value) => {
      if (!this.techScene) {
        return
      }

      if (value) {
        let boundary = this.techScene.getObjectByName('collisionBoundary')
        if (boundary) {
          this.scene.add(boundary.clone())
        }
        this.collisionPolyRestrictIn.map((poly, index) => {
          let collisionBoundary = this.techScene.getObjectByName('restrictIn_' + index)
          if (collisionBoundary) {
            this.scene.add(collisionBoundary.clone())
          }
        })
      } else {
        let boundary = this.scene.getObjectByName('collisionBoundary')
        if (boundary) {
          this.scene.remove(boundary)
        }
        this.collisionPolyRestrictIn.map((poly, index) => {
          let collisionBoundary = this.scene.getObjectByName('restrictIn_' + index)
          if (collisionBoundary) {
            this.scene.remove(collisionBoundary)
          }
        })
      }
    })

    let envTest = {
      clearEnvMaps: () => {
        this.scene.traverse(child => {
          if (child.isMesh) {
            if (child.material) {
              if (Array.isArray(child.material)) {
                child.material.forEach(material => {
                  material.envMap = null
                  material.needsUpdate = true
                })
              } else {
                child.material.envMap = null
                child.material.needsUpdate = true
              }
            }
          }
        })
      },
      clearAoMaps: () => {
        this.scene.traverse(child => {
          if (child.isMesh) {
            if (child.material) {
              if (Array.isArray(child.material)) {
                child.material.forEach(material => {
                  material.aoMap = null
                  material.needsUpdate = true
                })
              } else {
                child.material.aoMap = null
                child.material.needsUpdate = true
              }
            }
          }
        })
      },
      clearLightMaps: () => {
        this.scene.traverse(child => {
          if (child.isMesh) {
            if (child.material) {
              if (Array.isArray(child.material)) {
                child.material.forEach(material => {
                  material.lightMap = null
                  material.needsUpdate = true
                })
              } else {
                child.material.lightMap = null
                child.material.needsUpdate = true
              }
            }
          }
        })
      },
      clearEmissiveMaps: () => {
        this.scene.traverse(child => {
          if (child.isMesh) {
            if (child.material) {
              if (Array.isArray(child.material)) {
                child.material.forEach(material => {
                  material.emissiveMap = null
                  material.needsUpdate = true
                })
              } else {
                child.material.emissiveMap = null
                child.material.needsUpdate = true
              }
            }
          }
        })
      },
      grayoutMaterials: () => {
        this.scene.traverse(child => {
          if (child.isMesh) {
            if (child.material) {
              if (Array.isArray(child.material)) {
                child.material.forEach(material => {
                  if (material.color) {
                    material.color.setScalar(0.5)
                  }
                  material.map = null
                  material.needsUpdate = true
                })
              } else {
                if (child.material.color) {
                  child.material.color.setScalar(0.5)
                }
                child.material.map = null
                child.material.needsUpdate = true
              }
            }
          }
        })
      },
      resetMaterials: () => {
        let resetMaterial = new THREE.MeshPhongMaterial({color: '#7f7f7f'})

        this.scene.traverse(child => {
          if (child.isMesh) {
            if (child.material) {
              if (Array.isArray(child.material)) {
                for (let i = 0; i < child.material.length; i++) {
                  child.material[i] = resetMaterial
                }
              } else {
                child.material = resetMaterial
              }
            }
          }
        })
      },
      checkReusedGeometriesAndMaps: () => {
        let stats = {
          geometries: { total: 0, reused: 0, reused_ratio: 0, total_size: 0, reused_size: 0, rr_size: 0, sizes: {}, data: {} },
          materials:  { total: 0, reused: 0, reused_ratio: 0, total_size: 0, reused_size: 0, rr_size: 0, sizes: {}, data: {} },
          textures:   { total: 0, reused: 0, reused_ratio: 0, total_size: 0, reused_size: 0, rr_size: 0, sizes: {}, data: {} }
        }

        let collectTexture = texture => {
          if (!texture) {
            return
          }

          stats.textures.total++
          if (typeof stats.textures.data[texture.uuid] !== 'undefined') {
            stats.textures.data[texture.uuid]++
          } else {
            stats.textures.data[texture.uuid]=1
            // stats.textures.sizes[texture.uuid]=1
          }
        }

        let collectMaterial = material => {
          if (!material) {
            return
          }

          if (Array.isArray(material)) {
            material.forEach(collectMaterial)
          } else {
            stats.materials.total++
            if (typeof stats.materials.data[material.uuid] !== 'undefined') {
              stats.materials.data[material.uuid]++
            } else {
              stats.materials.data[material.uuid]=1
            }

            collectTexture(material.map)
            collectTexture(material.aoMap)
            collectTexture(material.lightMap)
            collectTexture(material.envMap)
          }
        }

        this.scene.traverse(child => {
          if (child.isMesh) {
            stats.geometries.total++
            stats.geometries.total_size += child.geometry.attributes.position.count

            if (stats.geometries.data[child.geometry.uuid]) {
              stats.geometries.data[child.geometry.uuid]++
            } else {
              stats.geometries.data[child.geometry.uuid] = 1
              stats.geometries.sizes[child.geometry.uuid] = child.geometry.attributes.position.count
            }

            collectMaterial(child.material)
          }
        })

        Object.keys(stats.geometries.data).forEach(gk => {
          if (stats.geometries.data[gk]>1) {
            stats.geometries.reused += stats.geometries.data[gk]
            stats.geometries.reused_size += stats.geometries.sizes[gk]
          }
        })
        Object.keys(stats.materials.data).forEach(gk => {
          if (stats.materials.data[gk]>1) stats.materials.reused+=stats.materials.data[gk]
        })
        Object.keys(stats.textures.data).forEach(gk => {
          if (stats.textures.data[gk]>1) stats.textures.reused+=stats.textures.data[gk]
        })

        stats.geometries.reused_ratio = ((stats.geometries.reused / stats.geometries.total) * 100).toFixed(2)
        stats.geometries.rr_size = ((stats.geometries.reused_size / stats.geometries.total_size) * 100).toFixed(2)
        stats.materials.reused_ratio  = ((stats.materials.reused / stats.materials.total) * 100).toFixed(2)
        stats.textures.reused_ratio   = ((stats.textures.reused / stats.textures.total) * 100).toFixed(2)

        console.table(stats)
      },
      updateCams: () => {
        this.requestCubeCamsUpdate()
      },
      testCubeCam: () => {
        let sphere = this.scene.getObjectByName('test sphere')
        if (!sphere) {
          var materialPhongCube = new THREE.MeshPhongMaterial({ shininess: 50, color: 0xffffff, specular: 0x999999 })
          var materialLambertCube = new THREE.MeshLambertMaterial({
            color: 0xffffff
          })

          sphere = new THREE.Mesh(new THREE.SphereBufferGeometry(20, 64, 32), materialPhongCube)
          // sphere = new THREE.Mesh(new THREE.BoxBufferGeometry(100, 100, 100), materialPhongCube)
          sphere.name = 'test sphere'
          sphere.position.set(250, 50, -700)
          this.scene.add(sphere)
        }

        this.scene.traverse(object => {
          if (object === this.debugSelectedObject) {
            let cubeCamera = null
            if (object.userData.cubeCamera) {
              cubeCamera = object.userData.cubeCamera
            }

            if (!cubeCamera) {
              console.error('cube camera not found!')
              return
            }

            let position = new THREE.Vector3()
            object.getWorldPosition(position)

            sphere.position.copy(cubeCamera.position)

            sphere.material.envMap = cubeCamera.renderTarget.texture
          }
        })
      }
    }

    let scenery = {
      loadNextModel: () => {
        this.loadNextModel(true)
      },
      floodNumber: 10,
      floodWithSelected: () => {
        // if (index === 0 && data.id === 3) {
        //   const xSpread = 350
        //   const zSpread = 750
        //   const ySpread = 100
        //   for (let i=0; i<100;i++) {
        //     let clone = instanceShiftGroup.clone()
        //
        //     clone.position.x = xSpread * 2 * Math.random() - xSpread
        //     clone.position.z = zSpread * 2 * Math.random() - zSpread
        //     clone.position.y += ySpread * 2 * Math.random() - ySpread
        //
        //     clone.rotation.y = Math.random() * Math.PI
        //
        //     this.scene.add(clone)
        //   }
        // }
      }
    }



    this.debugGui.add(envTest, 'resetMaterials')
    this.debugGui.add(envTest, 'grayoutMaterials')
    this.debugGui.add(envTest, 'clearLightMaps')
    this.debugGui.add(envTest, 'clearAoMaps')
    this.debugGui.add(envTest, 'clearEnvMaps')
    this.debugGui.add(envTest, 'clearEmissiveMaps')
    this.debugGui.add(envTest, 'testCubeCam')
    this.debugGui.add(envTest, 'updateCams')
    this.debugGui.add(envTest, 'checkReusedGeometriesAndMaps')
    this.debugGui.add(scenery, 'loadNextModel')

    let cameraFolder = this.debugGui.addFolder('Camera')
    cameraFolder.add(this.camera.position, 'y', -400, 400)

    let floodFolder = this.debugGui.addFolder('Flood')
    floodFolder.add(scenery, 'floodNumber', 1, 1000)
    floodFolder.add(scenery, 'floodWithSelected')

    window.setDebugSelectedObject = this.setDebugSelectedObject
  }

  onViewportContextMenu(event) {
    event.preventDefault()

    if (this.debugGui) {
      const ignoreNames = ['movementCursor', 'walls', 'panorama', 'fakeRoof', 'skyDome']

      let point = this.mousePosition
      let mouse = new THREE.Vector2((point.x * 2) - 1, -(point.y * 2) + 1)
      this.interactiveRaycaster.setFromCamera(mouse, this.camera)
      let intersections = this.interactiveRaycaster.intersectObjects(this.scene.children, true)
      let validIntersection = intersections.find(value => {
        return ignoreNames.indexOf(value.object.name) === -1
      })

      if (!validIntersection) {
        this.setDebugSelectedObject(null)
        return
      }

      let topmost = validIntersection.object
      validIntersection.object.traverseAncestors(parent => {
        if (!(parent instanceof THREE.Scene)) {
          topmost = parent
        }
      })
      if (ignoreNames.indexOf(topmost.name) !== -1) {
        return
      }

      this.setDebugSelectedObject(topmost)
    }
  }

  initEvents() {
    super.initEvents()

    this.onPanoramaClick = this.onPanoramaClick.bind(this)
    this.viewportContainer.addEventListener('panoramaClick', this.onPanoramaClick)

    this.viewportContainer.oncontextmenu = this.onViewportContextMenu.bind(this)
  }

  initLights() {
    super.initLights()

    //this.scene.add(new THREE.AmbientLight('#ffffff', 1))
  }

  initMesh() {
    this.buildCollisionRestrictions()
  }

  initControls() {
    super.initControls()

    // zoom must be disabled in 3D Scene
    if (this.controls) {
      this.controls.enableZoom = false
    }
  }

  initCamera() {
    super.initCamera()
  }

  setCameraPosition(data) {
    if (!data) {
      window.Logger.error('Position data is required', data)
      throw new Error('Position data is required')
    }

    let desiredPosition = this.camera.position.clone()

    if (typeof data.x !== 'undefined') {
      let newX = parseFloat(data.x)
      if (!isNaN(newX)) {
        desiredPosition.x = newX
      }
    }
    if (typeof data.y !== 'undefined') {
      let newZ = parseFloat(data.y)
      if (!isNaN(newZ)) {
        desiredPosition.z = newZ
      }
    }

    if (!desiredPosition.equals(this.camera.position)) {
      let collisionCheck = this.checkCollision(this.camera.position.clone(), desiredPosition)
      if (collisionCheck.failed) {
        desiredPosition.copy(collisionCheck.correctedPosition)
      }

      this.camera.position.copy(desiredPosition)
    }

    if (typeof data.lat !== 'undefined') {
      let newLat = parseFloat(data.lat)
      if (!isNaN(newLat)) {
        this.lat = newLat
      }
    }
    if (typeof data.lon !== 'undefined') {
      let newLon = parseFloat(data.lon)
      if (!isNaN(newLon)) {
        this.lon = newLon
      }
    }
  }

  destroy() {
    super.destroy()

    if (this.debugGui) {
      this.debugGui.destroy()
    }

    this.pictureTemplateObject = null
    this.movementCursor = null
    this.floorPlane = null
    this.interactiveObjects = []
    this.cubeCams = []
    this.doorAnimation = { enter: {}, leave: {} }

    this.techScene.children.map(object => this.techScene.remove(object))
    this.techScene = null

    this.skyParameters = null
  }

  unbindEvents() {
    super.unbindEvents()

    this.viewportContainer.removeEventListener('panoramaClick', this.onPanoramaClick)
    this.viewportContainer.oncontextmenu = null
  }

  loadModels() {
    let fbxLoader = new FBXLoader(this.loadingManager)
    fbxLoader.load(
      this.config.models.room.path,
      object => {
        this.onGalleryModelObjectLoad(object, this.config.models.room, () => { this.loadNextModel() })
      },
      data => this.onModelObjectLoadProgress(data),
      error => this.onModelObjectLoadError(error)
    )
  }

  loadNextModel(force = false) {
    // console.log('loadNextModel', this.loadingModelIndex)
    if (!this.config.models.objects.length || window.Config.options.debug.blockFurnitureLoading && !force) {
      return
    }

    if (typeof this.loadingModelIndex === 'undefined') {
      this.loadingModelIndex = 0
    } else {
      this.loadingModelIndex++
    }

    let data = this.config.models.objects[this.loadingModelIndex]
    if (!data) {
      return
    }

    if (!!data.blocked) {
      console.error('skipped object(blocked)', data.name, data.id)
      this.loadNextModel()
      return
    }

    if (data.path) {
      if (Array.isArray(data.path)) {
        let object = new THREE.Group()

        let loadPromises = data.path.map(path => {
          return new Promise(resolve => {
            let loader = path.split('.').pop() === 'glb' ? new GLTFLoader(this.loadingManager) : new FBXLoader(this.loadingManager)
            loader.load(
              path,
              loadedObject => {
                object.add(loadedObject)
                resolve()
              },
              data => this.onModelObjectLoadProgress(data, data),
              error => this.onModelObjectLoadError(error, data)
            )
          })
        })

        Promise.all(loadPromises).then(() => {
          this.onModelObjectLoad(object, data, () => {
            // console.log('model object load finished.')
            this.loadNextModel()
          })
        })

      } else {
        let loader = data.path.split('.').pop() === 'glb' ? new GLTFLoader(this.loadingManager) : new FBXLoader(this.loadingManager)

        loader.load(
          data.path,
          object => {
            this.onModelObjectLoad(object, data, () => {
              // console.log('model object load finished.')
              this.loadNextModel()
            })
          },
          data => this.onModelObjectLoadProgress(data, data),
          error => this.onModelObjectLoadError(error, data)
        )
      }
    }
  }

  initExhibitPoints() {
    if (window.Config.options.debug.blockBrandExhibitsLoading) {
      return
    }

    if (!window.Config.scenes.templatesRescaled) {
      const TEMPLATES_SCALE = window.Config.options.templates.templatesScale

      window.Config.scenes.templatesRescaled = true
      /**
       * rescale templates sizes and positions values to full size of exhibit
       */
      window.Config.scenes.templates.map((template) => {
        template.slots.map((slot) => {
          slot.placement.position.x *= TEMPLATES_SCALE
          slot.placement.position.y *= TEMPLATES_SCALE
          slot.placement.position.z *= TEMPLATES_SCALE

          slot.size.x *= TEMPLATES_SCALE
          slot.size.y *= TEMPLATES_SCALE
          slot.size.z *= TEMPLATES_SCALE
        })
      })
    }

    //let config = this.getSceneConfig()
    let exhibits = window.Config.scenes.embedded
    if (exhibits && exhibits.length) {
      let i = 0
      let renderNext = () => {
        this.createExhibitPoint(exhibits[i])
          .finally(() => {
            i++
            if (i < exhibits.length) {
              renderNext()
            }
          })
      }

      renderNext()
    }
  }

  createExhibitPoint(exhibitConfig) {
    let templateConfig = window.Config.scenes.templates.find(
      itemConfig => itemConfig.id === exhibitConfig.template
    )

    if (!templateConfig) {
      console.warn(
        'Interactive object setup skipped. Template config not found.',
        exhibitConfig.template,
        exhibitConfig
      )
      return Promise.resolve()
    }

    let slotsPromises = []

    exhibitConfig.slots.map((slotConfig) => {
      let templateSlotConfig = templateConfig.slots.find(
        slot => slot.id === slotConfig.id
      )
      if (!templateConfig) {
        console.warn(
          'Interactive object sub slot setup skipped. Slot config not found.',
          slotConfig.id,
          slotConfig,
          exhibitConfig
        )
        return Promise.resolve()
      }

      slotsPromises.push(this.createSlotObject(slotConfig, templateSlotConfig))
    })

    return Promise.all(slotsPromises).then((results) => {
      const TEMPLATES_SCALE = window.Config.options.templates.templatesScale
      let exhibit = new THREE.Group()
      exhibit.name = 'exhibit-' + exhibitConfig.id

      results.map((slotData) => {
        let object = slotData[0]
        let slotConfig = slotData[1]
        let templateSlotConfig = slotData[2]

        exhibit.add(object)

        object.position.set(
          templateSlotConfig.placement.position.x + templateSlotConfig.size.x / 2 - 50 * TEMPLATES_SCALE,
          -(templateSlotConfig.placement.position.y + templateSlotConfig.size.y / 2 - 50 * TEMPLATES_SCALE),
          templateSlotConfig.placement.position.z)

        object.rotation.set(
          THREE.Math.degToRad(templateSlotConfig.placement.rotation.x),
          THREE.Math.degToRad(templateSlotConfig.placement.rotation.y),
          THREE.Math.degToRad(templateSlotConfig.placement.rotation.z)
        )

        if (slotConfig.content.placement) {
          if (slotConfig.content.placement.position) {
            object.position.add(new THREE.Vector3(slotConfig.content.placement.position.x, slotConfig.content.placement.position.y, slotConfig.content.placement.position.z))
          }

          if (slotConfig.content.placement.rotation) {
            object.rotation.x += THREE.Math.degToRad(slotConfig.content.placement.rotation.x)
            object.rotation.y += THREE.Math.degToRad(slotConfig.content.placement.rotation.y)
            object.rotation.z -= THREE.Math.degToRad(slotConfig.content.placement.rotation.z)
          }
        }

        if (slotConfig.content.scale) {
          object.scale.multiplyScalar(slotConfig.content.scale)
        }
      })

      // TODO: template debug
      /*let debugPlane = new THREE.Mesh( new THREE.PlaneBufferGeometry(100 * TEMPLATES_SCALE,100 * TEMPLATES_SCALE) )
      debugPlane.position.z = -0.1
      exhibit.add( debugPlane )*/

      exhibit.position.set(exhibitConfig.placement.position.x, exhibitConfig.placement.position.y, exhibitConfig.placement.position.z)
      exhibit.rotation.set(
        THREE.Math.degToRad(exhibitConfig.placement.rotation.x),
        THREE.Math.degToRad(exhibitConfig.placement.rotation.y),
        THREE.Math.degToRad(exhibitConfig.placement.rotation.z)
      )

      this.scene.add(exhibit)
    })
  }

  createSlotObject(slotConfig, templateSlotConfig) {
    switch (slotConfig.type) {
      case 'logo':
        return this.createSlotTypeLogo(slotConfig, templateSlotConfig)
      case 'preview':
        return this.createSlotTypePreview(slotConfig, templateSlotConfig)
      case 'description':
        return this.createSlotTypeDescription(slotConfig, templateSlotConfig)
    }

    console.warn('Unhandled slot type', slotConfig.type, slotConfig)
    return Promise.resolve()
  }

  createSlotTypeLogo(slotConfig, templateSlotConfig) {
    return new Promise(resolve => {
      this.createContentTypeTexture(slotConfig, templateSlotConfig).then(object => {
        object.name = 'logo-' + slotConfig.id

        resolve([object, slotConfig, templateSlotConfig])
      })
    })
  }

  createSlotTypePreview(slotConfig, templateSlotConfig) {
    return new Promise(resolve => {
      this.createContentTypeTexture(slotConfig, templateSlotConfig).then(object => {
        // setup interactive behaviour
        // create glow

        // let glowPlane = this.createGlowBox(templateSlotConfig.size.x, templateSlotConfig.size.y, 10)
        // glowPlane.name = 'glow'
        // glowPlane.position.z = -0.1
        // glowPlane.userData.skipInteractions = true
        //
        // object.add(glowPlane)

        object.name = 'preview-' + slotConfig.id
        object.userData.slotConfig = slotConfig
        object.userData.title = slotConfig.content.embedded.tooltip
        object.userData.glowType = 'box'
        object.userData.glowBoxSize = templateSlotConfig.size

        this.registerInteractiveObject(object)

        resolve([object, slotConfig, templateSlotConfig])
      })
    })
  }

  createContentTypeTexture(slotConfig, templateSlotConfig) {
    return new Promise(resolve => {
      let textureLoader = new THREE.TextureLoader(this.loadingManager)
      let baseColor = new THREE.Color(this.config.colors.exhibitsBaseColor)

      //let imageTexture = textureLoader.load(slotConfig.content.sourceFile)
      let textureLoaded = new Promise(resolveTextureLoad => {
        if (slotConfig.content.type === 'video') {
          let video = document.createElement('video')

          let imageTexture = new THREE.VideoTexture(video)
          imageTexture.minFilter = THREE.LinearFilter
          imageTexture.magFilter = THREE.LinearFilter
          imageTexture.format = THREE.RGBFormat

          video.addEventListener('loadedmetadata', () => {
            resolveTextureLoad([
              imageTexture,
              {
                width: video.videoWidth,
                height: video.videoHeight
              }
            ])
          })

          video.crossOrigin = 'anonymous'
          // video.width = templateSlotConfig.size.x
          // video.height = templateSlotConfig.size.y
          video.loop = true
          video.muted = true
          video.src = slotConfig.content.sourceFile
          video.setAttribute('webkit-playsinline', 'webkit-playsinline')
          video.play()
        } else {
          textureLoader.load(slotConfig.content.sourceFile, (imageTexture) => {
            resolveTextureLoad([
              imageTexture,
              {
                width: imageTexture.image.naturalWidth,
                height: imageTexture.image.naturalHeight
              }
            ])
          })
        }
      })

      textureLoaded.then((result) => {
        let imageTexture = result[0]
        let textureSize = result[1]

        let placementType = slotConfig.content.sourcePlacementType || 'stretch'
        let width = templateSlotConfig.size.x
        let height = templateSlotConfig.size.y

        let imageWidth = textureSize.width
        let imageHeight = textureSize.height
        let scaleW = width / imageWidth
        let scaleH = height / imageHeight

        if (placementType === 'fit') {
          let scale = Math.min(scaleW, scaleH)

          width = imageWidth * scale
          height = imageHeight * scale

        } else if (placementType === 'overflow') {
          let scale = Math.max(scaleW, scaleH)

          let oldWidth = width
          let oldHeight = height

          let newWidth = imageWidth * scale
          let newHeight = imageHeight * scale

          if (imageWidth > imageHeight) {
            let r2 = oldWidth / newWidth

            imageTexture.offset.x = (1 - r2) / 2
            imageTexture.repeat.x = r2

          } else {
            let r2 = oldHeight / newHeight

            imageTexture.offset.y = (1 - r2) / 2
            imageTexture.repeat.y = r2
          }
        }

        let imagePlaneGeometry = new THREE.PlaneBufferGeometry(width, height)

        let imageMaterial = new THREE.MeshBasicMaterial({
          color: baseColor,
          map: imageTexture,
          transparent: !window.Config.options.debug.brandExhibitsPlacement && slotConfig.content.type === 'image'
        })

        let object = new THREE.Mesh(imagePlaneGeometry, imageMaterial)
        object.position.z = 0.5

        this.geometries.push(imagePlaneGeometry)
        this.textures.push(imageTexture)
        this.materials.push(imageMaterial)

        resolve(object)
      })
    })
  }

  createSlotTypeDescription(slotConfig, templateSlotConfig) {
    return new Promise(resolve => {
      let baseColor = new THREE.Color('hsl(0, 0%, 40%)')
      let textTextureSize = window.Config.options.templates.textTextureDPU * templateSlotConfig.size.x
      if (textTextureSize > window.Config.options.templates.textTextureMaxSize) {
        textTextureSize = window.Config.options.templates.textTextureMaxSize
      } else if (!Number.isInteger(Math.log2(textTextureSize))) {
        let higherPower = Math.ceil(Math.log2(textTextureSize))
        textTextureSize = Math.pow(2, higherPower)
      }

      let zoom = 4
      let textSize = [templateSlotConfig.size.x, templateSlotConfig.size.y]
      let textRatio = textSize[1] / textSize[0]

      let options = {
        width: textTextureSize,
        height: textTextureSize * textRatio
      }

      // TODO: stretch UVs to match new texture size
      let oldHeight
      let stretchUVsIsNeeded = false

      if (!Number.isInteger(Math.log2(options.height))) {
        oldHeight = options.height
        stretchUVsIsNeeded = true
        let higherPower = Math.ceil(Math.log2(options.height))
        options.height = Math.pow(2, higherPower)
      }

      let textPlaneGeometry = new THREE.PlaneBufferGeometry(...textSize)

      if (stretchUVsIsNeeded) {
        let coeff = oldHeight / options.height
        // default UVs was: 0, 1, 1, 1, 0, 0, 1, 0
        textPlaneGeometry.attributes.uv.array[5] = (1 - coeff)
        textPlaneGeometry.attributes.uv.array[7] = (1 - coeff)

        textPlaneGeometry.attributes.uv.needsUpdate = true
      }

      let canvas = document.createElement('canvas')
      canvas.width = options.width
      canvas.height = options.height

      let html = ''
      // html += '<link href="https://fonts.googleapis.com/css?family=Indie+Flower" rel="stylesheet">'
      html += '<style type="text/css">' +
        'body { padding: 0; margin: 0 }' +
        '.preview-body { color: #000000; width: ' + Math.ceil(options.width / zoom) + 'px; zoom: ' + zoom + '; padding: 0; margin: 0; }' +
        'img.title { max-width: ' + Math.ceil(options.width / zoom) + 'px; }' +
        '</style>'
      html += '<div class="preview-body">'
      html += slotConfig.content.data
      html += '</div>'

      rasterizeHTML.drawHTML(html, canvas, options).then((renderResult) => {
        let textTexture = new THREE.Texture(canvas)
        textTexture.needsUpdate = true

        let textPlaneMaterial = new THREE.MeshPhongMaterial({
          color: baseColor,
          map: textTexture,
          transparent: !window.Config.options.debug.brandExhibitsPlacement
        })

        let textPlane = new THREE.Mesh(textPlaneGeometry, textPlaneMaterial)
        textPlane.position.z = 0.5
        textPlane.skipInteractions = true

        textPlane.name = 'description-' + slotConfig.id

        this.materials.push(textPlaneMaterial)
        this.geometries.push(textPlaneGeometry)
        this.textures.push(textTexture)

        resolve([textPlane, slotConfig, templateSlotConfig])
      })
    })
  }

  onInteractiveObjectClick(object) {
    if (object.name === 'doorLink') {
      // special case
      let win = window.open(this.config.roomConfiguration.door.link, '_blank')
      win.focus()
      return false
    }

    if (!this.cameraIsAtInteractivePosition(object)) {
      this.moveCameraToObject(object)
      return false
    }
  }

  cameraIsAtInteractivePosition(object) {
    const eps = 0.0000001

    let objectOverviewSpot = this.getObjectSpot(object)

    let positionMatch = this.camera.position.distanceTo(objectOverviewSpot.position) < 1
    let rotationMatch = Math.abs(objectOverviewSpot.rotation.y - this.camera.rotation.y) < eps

    return positionMatch && rotationMatch
  }

  getObjectSpot(object, distance = 240) {
    let worldQuaternion = new THREE.Quaternion()
    object.getWorldQuaternion(worldQuaternion)

    let worldEuler = new THREE.Euler()
    worldEuler.setFromQuaternion(worldQuaternion)
    worldEuler.x = 0
    worldEuler.z = 0

    let targetRotation = new THREE.Vector3(0, worldEuler.y, 0)
    let targetPosition = object.getWorldPosition(new THREE.Vector3())
    targetPosition.y = this.camera.position.y // reset elevation change

    let frameToTargetOrientation = new THREE.Vector3(0, 0, 1)
    frameToTargetOrientation.applyEuler(worldEuler)

    targetPosition.add(frameToTargetOrientation.multiplyScalar(distance))

    return { position: targetPosition, rotation: targetRotation }
  }

  moveCameraToObject(object, distance = 240, time = 2000) {
    if (this.moveByClickTween) {
      this.stopMoveByTween()
    }

    return new Promise(resolve => {
      if (this.rotationTween) {
        TWEEN.remove(this.rotationTween) // delete previous
      }
      if (this.movementTween) {
        TWEEN.remove(this.movementTween) // delete previous
      }

      this.movementTween = new TWEEN.Tween(this.camera.position)
      this.movementTween.easing(TWEEN.Easing.Cubic.InOut)
      this.movementTween.onComplete(() => {
        this.moveSpeed = 0
        this.enableControls()
        //this.toggleAutoRotatePause(true, true)
        resolve()
        this.sendMovementDataEvent()
      })

      // setup move
      let moveSpeed = 0.12
      let objectOverviewSpot = this.getObjectSpot(object, distance)
      let targetPosition = objectOverviewSpot.position

      let moveDistance = this.camera.position.distanceTo(targetPosition)
      let movementTime = Math.min(time, moveDistance / moveSpeed)

      // setup rotation
      if (this.lon > 180) {
        this.lon -= 360
      }

      let rotationSpeed = 0.02
      let frameTargetEul = new THREE.Euler() // setFromVector3
      frameTargetEul.setFromVector3(objectOverviewSpot.rotation)
      let targetRotation = (90 + THREE.Math.radToDeg(frameTargetEul.y)) % 360

      let rotationAngle = targetRotation - this.lon + 180
      rotationAngle = (rotationAngle - Math.floor(rotationAngle / 360) * 360) - 180

      targetRotation = this.lon + rotationAngle

      let rotationTime = Math.abs(this.lon - targetRotation) / rotationSpeed


      this.rotationTween = new TWEEN.Tween({ lat: this.lat, lon: this.lon })
      this.rotationTween.easing(TWEEN.Easing.Cubic.InOut)
      this.rotationTween.onUpdate((value) => {
        this.lat = value.lat
        this.lon = value.lon
      })


      let animationsTime = Math.max(rotationTime, movementTime)

      this.movementTween.to(targetPosition, animationsTime)
      this.rotationTween.to({ lat: 0, lon: targetRotation }, animationsTime)


      // start animations
      this.moveSpeed = 0
      this.disableControls()
      //this.toggleAutoRotatePause(true, false)
      this.movementTween.start()
      this.rotationTween.start()
    })
  }

  initFramesAnimations() {
    this.initFrameEnterAnimation()
    this.initFrameLeaveAnimation()
  }

  initFrameEnterAnimation() {
  }

  initFrameLeaveAnimation() {
  }

  animationOnEmbeddedOpen(config) {
    this.moveSpeed = 0
    this.disableControls()
    this.toggleAutoRotatePause(true, false)

    this.embeddedViewOptions.frameMode = true
    this.embeddedViewOptions.show = true

    setTimeout(() => {
      this.embeddedViewOptions.frameMode = false
    }, 1)

    this.render()

    this.enableControls()
  }

  animationOnEmbeddedLeave(config) {
    return new Promise((resolve) => {
      this.embeddedViewOptions.frameMode = true

      setTimeout(() => { // size transition
        // this.embeddedViewOptions.show = false

        this.enableControls()
        this.toggleAutoRotatePause(true, true)

        resolve()
      }, 500)

      // clean frame mode
      setTimeout(() => {
        this.embeddedViewOptions.frameMode = false
      }, 1000)
    })
  }

  registerInteractiveObject(object) {
    object.userData.interactiveParent = object
    object.traverse((child) => {
      if (child.isMesh && !child.userData.skipInteractions) {
        child.userData.interactiveParent = object
        this.interactiveObjects.push(child)
      }
    })
  }

  beforeRenderSceneLoop() {
    this.initMesh()
  }

  buildCollisionRestrictions() {
    let allowedX = this.config.roomConfiguration.size.width / 2 - this.config.roomConfiguration.padding
    let allowedY = this.config.roomConfiguration.size.length / 2 - this.config.roomConfiguration.padding
    this.collisionPolyRestrictOut = [
      [-allowedX, allowedY],
      [allowedX, allowedY],
      [allowedX, -allowedY],
      [-allowedX, -allowedY]
    ]

    this.collisionPolyRestrictIn = []

    this.config.roomConfiguration.columns.positions.map(pos => {
      // create column points
      let colX = this.config.roomConfiguration.columns.size.width / 2
      let colY = this.config.roomConfiguration.columns.size.length / 2
      let poly = []

      let arcHalfLength = colY + this.config.roomConfiguration.columns.paddingBetween
      let steps = 7
      for (let i = 0; i <= steps; i++) {
        let pr = i / steps
        let x = colX + this.config.roomConfiguration.columns.paddingBetween + Math.sin(Math.PI * pr) * this.config.roomConfiguration.columns.paddingFrontRadius + this.config.roomConfiguration.columns.paddingFront
        let y = arcHalfLength - arcHalfLength * pr * 2

        poly.push([x, y])
      }

      for (let i = 0; i <= steps; i++) {
        let pr = i / steps
        let x = -colX - this.config.roomConfiguration.columns.paddingBetween - Math.sin(Math.PI * pr) * this.config.roomConfiguration.columns.paddingFrontRadius - this.config.roomConfiguration.columns.paddingFront
        let y = -arcHalfLength + arcHalfLength * pr * 2

        poly.push([x, y])
      }

      poly.map((p, i) => {
        poly[i][0] += pos.x
        poly[i][1] += pos.y
      })

      this.collisionPolyRestrictIn.push(poly)
    })
  }

  onModelObjectLoad(object, data = null, onComplete) {
    if (!!data) {
      window.Logger.log('MODEL OBJECT LOAD', data.name)
    }
    window.setTimeout(() => {
      this.processModelObject(object, data, onComplete)
    }, 0)
  }

  processModelObject(object, data = null, onComplete) {
    const loadAoMaps = true
    const loadLightMaps = true
    const loadEmissiveMap = true
    const useEnvCameras = false

    if (object.scene) {
      // gltf loaded
      object = object.scene.children[0]
    } else {
      if (data.name && data.name.substr(0, 3) === 'TMP') {

      } else {
        if (!data.composite && !Array.isArray(data.path)) {
          object = object.children[0]
        }
      }
    }

    let shiftGroup = new THREE.Group()
    if (data && data.name) {
      shiftGroup.name = data.name
    }
    shiftGroup.add(object)
    object.position.set(0, 0, 0)

    object.traverse(child => {
      if (child.isMesh) {
        if (child.geometry.attributes.uv !== undefined) {
          child.geometry.attributes.uv2 = child.geometry.attributes.uv.clone()
        }
      }
    })

    const overrideMaterialsProps = {
      'al': {
        reflectivity: 0.5
      },
      '01 - Default': {
        reflectivity: 0.5
      },
      'black met': {
        combine: THREE.AddOperation,
        reflectivity: 0.3
      },
      'black met 2': {
        combine: THREE.AddOperation,
        reflectivity: 0.3
      },
      'glass matte': {
        transparent: false
      }
    }

    const processMaterial = (material) => {
      if (typeof overrideMaterialsProps[material.name] !== 'undefined') {
        Object.assign(material, overrideMaterialsProps[material.name])
      }

      if (material.map !== null || material.color.equals(failedColor)) {
        material.color.setRGB(1, 1, 1)
      }
    }

    let failedColor = new THREE.Color(0.2, 0, 1)
    object.traverse(child => {
      if (child.isMesh) {
        if (child.material.length) {
          // console.error("ARRAY material", child.material.length, data.name)
          child.material.forEach(processMaterial)
        } else {
          processMaterial(child.material)
        }
      }
    })

    if (data.name && data.name.substr(0, 3) === 'TMP') {
      let overrideMaterial = new THREE.MeshBasicMaterial({ color: '#ff00ff' })
      object.traverse(child => {
        if (child.isMesh) {
          child.material = overrideMaterial
        }
      })
    }

    if (data && data.aoMap && loadAoMaps) {
      //object.material.aoMap = new THREE.TextureLoader().load(data.aoMap)

      new THREE.TextureLoader().load(data.aoMap, (texture) => {
        shiftGroup.traverse(child => {
          if (child.isMesh) {
            if (child.material.length) {
              child.material.forEach(function (material) {
                material.aoMap = texture
                material.needsUpdate = true
                if (material.map !== null) {
                  material.color.setRGB(1, 1, 1)
                }
              })
            } else {
              child.material.aoMap = texture
              child.material.needsUpdate = true
            }
          }
        })
      })
    }

    if (data && data.lightMap && loadLightMaps) {
      let lightMapTexture = new THREE.TextureLoader().load(data.lightMap)
      shiftGroup.traverse(child => {
        if (child.isMesh) {
          if (child.material.length) {
            child.material.forEach(function (material) {
              material.lightMap = lightMapTexture
              material.needsUpdate = true
            })
          } else {
            child.material.lightMap = lightMapTexture
            child.material.needsUpdate = true
          }
        }
      })
    }

    if (data && data.emissiveMap && loadEmissiveMap) {
      let emissiveMapTexture = new THREE.TextureLoader().load(data.emissiveMap)
      shiftGroup.traverse(child => {
        if (child.isMesh) {
          if (child.material.length) {
            child.material.forEach(function (material) {
              material.color.setScalar(0)
              material.emissive.setScalar(1)
              material.emissiveMap = emissiveMapTexture
              if (material.emissiveIntensity === 0) {
                material.emissiveIntensity = 1
              }
              material.needsUpdate = true
            })
          } else {
            child.material.color.setScalar(0)
            child.material.emissive.setScalar(1)
            child.material.emissiveMap = emissiveMapTexture
            if (child.material.emissiveIntensity === 0) {
              child.material.emissiveIntensity = 1
            }
            child.material.needsUpdate = true
          }
        }
      })
    }

    let castShadow = data.castShadow !== false
    let receiveShadow = data.receiveShadow !== false
    shiftGroup.traverse(child => {
      if (child.isMesh) {
        child.castShadow = castShadow
        child.receiveShadow = receiveShadow
      }
    })

    // if (data && data.lights) {
    //   data.lights.forEach(lightConfig => {
    //     this.initLight(lightConfig, shiftGroup)
    //   })
    // }

    if (data && data.position) {
      shiftGroup.position.set(data.position.x, data.position.y, data.position.z)
    }
    if (data && data.rotation) {
      shiftGroup.rotation.set(
        THREE.Math.degToRad(data.rotation.x),
        THREE.Math.degToRad(data.rotation.y),
        THREE.Math.degToRad(data.rotation.z)
      )
    }
    if (data && data.scale) {
      shiftGroup.scale.setScalar(data.scale)
    } else {
      shiftGroup.scale.multiplyScalar(0.1)
    }

    let envCameraConfig = {
      resolution: 128,
      near: 0.2,
      far: 1800,
      capturePoint: { x: 0, y: 0, z: 0 }
    }

    if (data.envMapCamera && useEnvCameras) {
      // prepare common data for all instances,
      // overall envMap config and env camera point offset

      Object.assign(envCameraConfig, data.envMapCamera)

      // precalculate capturePoint, if not provided
      if (!data.envMapCamera.capturePoint) {
        let capturePoint = new THREE.Vector3()

        // detect center of geometry to use as cubeCamera position
        let boundingSpheresCenters = []
        object.traverse(child => {
          if (child.isMesh) {
            child.geometry.computeBoundingSphere()
            boundingSpheresCenters.push(child.localToWorld(child.geometry.boundingSphere.center.clone()))
          }
        })

        if (boundingSpheresCenters) {
          if (boundingSpheresCenters.length === 1) {
            capturePoint.copy(boundingSpheresCenters[0])
          } else {
            // if there is more than one geometry get center of their centers
            let boundingSphere = new THREE.Sphere()
            boundingSphere.setFromPoints(boundingSpheresCenters)
            capturePoint.copy(boundingSphere.center)
          }
          capturePoint.sub(shiftGroup.position)
        }

        envCameraConfig.capturePoint = capturePoint
      }
    }

    if (data.correction) {
      // reposition geometry got from file,
      // this is needed if object in file is not centered properly

      if (data.correction.position) {
        object.position.copy(new THREE.Vector3(
          data.correction.position.x,
          data.correction.position.y,
          data.correction.position.z
          )
        )
      }

      if (data.correction.rotation) {
        object.rotation.set(
          THREE.Math.degToRad(data.correction.rotation.x),
          THREE.Math.degToRad(data.correction.rotation.y),
          THREE.Math.degToRad(data.correction.rotation.z)
        )
      }
    }

    if (data && data.instances) {
      // make clones of object and put in defined positions

      if (Array.isArray(data.instances) === false) {
        window.Logger.error('Object.instances config is not an array')
        throw new Error('Object.instances config is not an array')
      }

      data.instances.forEach((instance, index) => {
        // if (index > 0) {
        //   console.error('skipped', index)
        //   return
        // }

        let instanceShiftGroup = shiftGroup.clone()

        instanceShiftGroup.name = data.name + '#' + index

        instanceShiftGroup.position.set(instance.position.x, instance.position.y, instance.position.z)
        if (instance.rotation) {
          instanceShiftGroup.rotation.set(
            THREE.Math.degToRad(instance.rotation.x),
            THREE.Math.degToRad(instance.rotation.y),
            THREE.Math.degToRad(instance.rotation.z)
          )
        }

        let needsOwnMaterials = (instance.lightMap && loadLightMaps) || (data.envMapCamera && useEnvCameras)
        if (needsOwnMaterials) {
          //

          // clone mesh
          // let instObject = object.clone()
          // instanceShiftGroup.remove(object)
          // instanceShiftGroup.add(instObject)

          // clone materials if we need our own instances
          instanceShiftGroup.traverse(child => {
            if (child.isMesh) {
              if (Array.isArray(child.material)) {
                let newMaterial = []
                for (let i = 0; i < child.material.length; i++) {
                  newMaterial[i] = child.material[i].clone()
                }
                child.material = newMaterial
              } else {
                child.material = child.material.clone()
              }
            }
          })
        }

        if (instance.lightMap && loadLightMaps) {
          new THREE.TextureLoader().load(instance.lightMap, (texture) => {
            instanceShiftGroup.traverse(child => {
              if (child.isMesh) {
                if (child.material.length) {
                  child.material.forEach(function (material) {
                    material.lightMap = texture
                    material.needsUpdate = true
                  })
                } else {
                  child.material.lightMap = texture
                  child.material.needsUpdate = true
                }
              }
            })
          })
        }

        if (data.envMapCamera && useEnvCameras) {
          // materials that will use cubeCamera for "live" environment map
          let envMaterials = []
          instanceShiftGroup.traverse(child => {
            if (child.isMesh) {
              if (Array.isArray(child.material)) {
                for (var i = 0; i < child.material.length; i++) {
                  if (!data.envMapMaterials || data.envMapMaterials.indexOf(child.material[i].name) !== -1) {
                    envMaterials.push(child.material[i])
                  }
                }
              } else {
                if (!data.envMapMaterials || data.envMapMaterials.indexOf(child.material.name) !== -1) {
                  envMaterials.push(child.material)
                }
              }
            }
          })

          let cubeCamera = this._setupCubeCam(instanceShiftGroup, envCameraConfig)
          envMaterials.forEach(material => {
            material.envMap = cubeCamera.renderTarget.texture
          })
        }

        this.scene.add(instanceShiftGroup)

        if (data.interactive) {
          instanceShiftGroup.userData.objectViewConfig = data.interactive
          instanceShiftGroup.userData.glowType = 'outline'
          this.registerInteractiveObject(instanceShiftGroup)
        }
      })
    } else {
      if (data.envMapCamera && useEnvCameras) {
        // materials that will use cubeCamera for "live" environment map
        let envMaterials = []
        shiftGroup.traverse(child => {
          if (child.isMesh) {
            if (Array.isArray(child.material)) {
              child.material.forEach(material => {
                if (!data.envMapMaterials || data.envMapMaterials.indexOf(material.name) !== -1) {
                  envMaterials.push(material)
                }
              })
            } else {
              if (!data.envMapMaterials || data.envMapMaterials.indexOf(child.material.name) !== -1) {
                envMaterials.push(child.material)
              }
            }
          }
        })


        let cubeCamera = this._setupCubeCam(shiftGroup, envCameraConfig)
        envMaterials.forEach(material => {
          material.envMap = cubeCamera.renderTarget.texture
        })
      }

      if (data.interactive) {
        shiftGroup.userData.objectViewConfig = data.interactive
        shiftGroup.userData.glowType = 'outline'
        this.registerInteractiveObject(shiftGroup)
      }

      this.scene.add(shiftGroup)
    }

    onComplete()
  }

  _setupCubeCam(object, config) {
    let envMapCameraOffset = new THREE.Vector3(0, 0, 0)
    if (config.capturePoint) {
      envMapCameraOffset.set(config.capturePoint.x, config.capturePoint.y, config.capturePoint.z)
    }

    let cubeCamera = new THREE.CubeCamera(config.near, config.far, config.resolution)
    cubeCamera.name = 'cc-' + object.name
    cubeCamera.renderTarget.texture.generateMipmaps = true
    cubeCamera.renderTarget.texture.minFilter = THREE.LinearMipMapLinearFilter

    cubeCamera.position.copy(object.position).add(envMapCameraOffset)
    object.userData.cubeCamera = cubeCamera
    cubeCamera.userData.object = object

    this.cubeCams.push({
      camera: cubeCamera,
      object: object,
      needsUpdate: true
    })

    return cubeCamera
  }

  createDoorLinkObject() {
    let doorConfig = this.config.roomConfiguration.door
    let doorGroup = new THREE.Group()
    doorGroup.name = 'doorLink'
    doorGroup.userData.title = doorConfig.tooltip

    doorGroup.userData.glowType = 'box'
    doorGroup.userData.glowBoxSize = {
      x: doorConfig.size.width,
      y: doorConfig.size.height + 5  // added offset to move bottom part of glow under floor
    }
    doorGroup.userData.glowBoxPosition = {
      x: 0,
      y: -1.25,
      z: 0
    }

    let doorBoxMaterial = new THREE.MeshBasicMaterial({
      opacity: 0,
      transparent: true
    })
    let doorBoxGeometry = new THREE.PlaneBufferGeometry(doorConfig.size.width, doorConfig.size.height)
    let doorBox = new THREE.Mesh(doorBoxGeometry, doorBoxMaterial)
    doorGroup.add(doorBox)

    doorGroup.position.set(doorConfig.placement.position.x, doorConfig.placement.position.y, doorConfig.placement.position.z)
    doorGroup.rotation.set(
      THREE.Math.degToRad(doorConfig.placement.rotation.x),
      THREE.Math.degToRad(doorConfig.placement.rotation.y),
      THREE.Math.degToRad(doorConfig.placement.rotation.z)
    )
    this.registerInteractiveObject(doorGroup)

    this.scene.add(doorGroup)
  }

  onGalleryModelObjectLoad(object, data = null, onComplete) {
    window.setTimeout(() => {
      this._onGalleryModelObjectLoad(object, data, onComplete)
      this.requestCubeCamsUpdate()
    }, 0)
  }

  _onGalleryModelObjectLoad(object, data = null, onComplete) {
    if (!!data) {
      window.Logger.log('GALLERY MODEL OBJECT LOAD')
    }

    if (data.position) {
      object.position.set(data.position.x, data.position.y, data.position.z)
    }

    let walls = object.getObjectByName('walls')

    // set a bit lighter black
    walls.material[7].color.setScalar(0.15)
    walls.material[8].color.setScalar(0.15)
    walls.material[9].color.setScalar(0.15)

    let wallsMats = {
      floor: walls.material[1],
      ceil: walls.material[4],
      wallOutdoor: walls.material[0],
      wallBottom: walls.material[2],
      wallTop: walls.material[3],
      doorBottom: walls.material[5],
      doorTop: walls.material[6],
      doorHandle: walls.material[7],
      windowFrame: walls.material[8],
      grid: walls.material[9],
      windowGlass: walls.material[10],
    }
    wallsMats.ceil.color.setRGB(1, 1, 1)
    wallsMats.floor.color.setRGB(1, 1, 1)
    wallsMats.wallTop.color.setRGB(1, 1, 1)
    wallsMats.wallBottom.color.setRGB(1, 1, 1)
    walls.material.forEach(m => m.map !== null && m.color.setRGB(1, 1, 1))

    object.traverse(child => {
      if (child.isMesh && child.material) {
        child.castShadow = child.name !== 'glass'
        child.receiveShadow = child.name !== 'glass'

        child.geometry.addAttribute('uv2', new THREE.Float32BufferAttribute(child.geometry.attributes.uv.array, 2))
      }
    })

    if (data.aoMap) {
      new THREE.TextureLoader().load(data.aoMap, (texture) => {
        for (let i = 0; i < walls.material.length; i++) {
          if (walls.material[i] === wallsMats.windowGlass) {
            continue
          }

          walls.material[i].aoMap = texture
          walls.material[i].needsUpdate = true
        }
      })
    }

    new THREE.TextureLoader().load(data.lightMap, (texture) => {
      for (let i = 0; i < walls.material.length; i++) {
        if (walls.material[i] === wallsMats.windowGlass) {
          continue
        }

        walls.material[i].lightMap = texture
        walls.material[i].needsUpdate = true
      }
    })

    let techMaterial = new THREE.MeshBasicMaterial({
      color: '#0000ff',
      side: THREE.DoubleSide
    })

    let fakeRoof = new THREE.Mesh(new THREE.PlaneBufferGeometry(1500, 1700), techMaterial)
    fakeRoof.name = 'fakeRoof'
    fakeRoof.rotation.x = -Math.PI / 2
    fakeRoof.position.y = 330
    fakeRoof.castShadow = true
    fakeRoof.receiveShadow = false
    this.scene.add(fakeRoof)

    let shiftGroup = new THREE.Group()
    if (data && data.name) {
      shiftGroup.name = data.name
    }
    shiftGroup.add(object)
    object.position.set(0, 0, 0)
    if (data && data.position) {
      shiftGroup.position.set(data.position.x, data.position.y, data.position.z)
    }
    if (data && data.rotation) {
      shiftGroup.rotation.set(
        THREE.Math.degToRad(data.rotation.x),
        THREE.Math.degToRad(data.rotation.y),
        THREE.Math.degToRad(data.rotation.z)
      )
    }
    if (data && data.scale) {
      shiftGroup.scale.setScalar(data.scale)
    } else {
      shiftGroup.scale.multiplyScalar(0.1)
    }

    this.scene.add(shiftGroup)

    this.createDoorLinkObject()

    let collisionGeometry = new THREE.Geometry()
    this.collisionPolyRestrictOut.map(p => {
      collisionGeometry.vertices.push(new THREE.Vector3(p[0], 1, p[1]))
    })
    let collisionBoundary = new THREE.LineLoop(collisionGeometry)
    collisionBoundary.name = 'collisionBoundary'

    if (window.Config.options.debug.showCollisionsBoundaries) {
      this.scene.add(collisionBoundary)
    } else {
      this.techScene.add(collisionBoundary)
    }

    this.geometries.push(collisionGeometry)

    // collisionPolyRestrictIn
    this.collisionPolyRestrictIn.map((poly, index) => {
      let collisionGeometry = new THREE.Geometry()
      poly.map(p => {
        collisionGeometry.vertices.push(new THREE.Vector3(p[0], 1, p[1]))
      })
      let collisionBoundary = new THREE.LineLoop(collisionGeometry)
      collisionBoundary.name = 'restrictIn_' + index

      if (window.Config.options.debug.showCollisionsBoundaries) {
        this.scene.add(collisionBoundary)
      } else {
        this.techScene.add(collisionBoundary)
      }
      this.geometries.push(collisionGeometry)
    })

    // move plane
    let floorPlaneGeometry = new THREE.PlaneBufferGeometry(800, 1600)
    let floorPlaneMaterial = techMaterial
    this.floorPlane = new THREE.Mesh(floorPlaneGeometry, floorPlaneMaterial)
    this.floorPlane.rotation.x = -Math.PI / 2
    this.techScene.add(this.floorPlane)

    this.geometries.push(floorPlaneGeometry)
    this.materials.push(floorPlaneMaterial)

    let movementCursorMaterial = new THREE.MeshBasicMaterial({
      map: this.getMovementCursorTexture(),
      color: '#fff',
      transparent: true
    })
    let movementCursorGeometry = new THREE.PlaneBufferGeometry(30, 30)
    this.movementCursor = new THREE.Mesh(movementCursorGeometry, movementCursorMaterial)
    this.movementCursor.name = 'movementCursor'
    this.movementCursor.rotation.x = -Math.PI / 2
    this.movementCursor.visible = false
    this.scene.add(this.movementCursor)

    this.geometries.push(movementCursorGeometry)
    this.materials.push(movementCursorMaterial)

    this.renderer.render(this.techScene, this.camera)

    onComplete()
  }

  initBackgroundPanorama(config) {
    new THREE.TextureLoader().load(config.path, (texture) => {
      let panoSphere = new THREE.Mesh(new THREE.SphereBufferGeometry(1600, 32, 16), new THREE.MeshBasicMaterial({
        map: texture,
        side: THREE.BackSide
      }))
      panoSphere.name = 'panorama'
      this.scene.add(panoSphere)
    })
  }

  initBackgroundSky(config) {
    let light = this.scene.getObjectByName('sunLight')

    let sky = new Sky()
    sky.name = 'skyDome'
    sky.scale.setScalar(1600)
    this.scene.add(sky)

    let sunSphere = new THREE.Mesh(
      new THREE.SphereBufferGeometry(20, 16, 8),
      new THREE.MeshBasicMaterial({ color: 0xffffff })
    )
    sunSphere.visible = false
    sunSphere.name = 'sunSphere'
    sunSphere.position.y = -700000
    this.scene.add(sunSphere)

    let uniforms = sky.material.uniforms
    uniforms.turbidity.value = 10
    uniforms.rayleigh.value = 2
    uniforms.luminance.value = 1
    uniforms.mieCoefficient.value = 0.005
    uniforms.mieDirectionalG.value = 0.8
    this.skyParameters = {
      disable: false,
      sunColor: '#ff7e07',
      distance: 1400,
      inclination: 0.49,
      azimuth: 0.2912
    }

    this.skyParameters.setDay = () => {
      this.animateEnvironmentTo(this.config.environmentPresets.day)
    }
    this.skyParameters.setEvening = () => {
      this.animateEnvironmentTo(this.config.environmentPresets.lateEvening)
    }
    this.skyParameters.setNight = () => {
      this.animateEnvironmentTo(this.config.environmentPresets.night)
    }

    if (this.debugGui) {
      let folder = this.debugGui.addFolder('Sky')
      folder.add(this.skyParameters, 'disable').onChange(() => {
        sky.visible = !this.skyParameters.disable
        light.visible = !this.skyParameters.disable
        //sunSphere.visible = !this.skyParameters.disable

        this.requestCubeCamsUpdate()
      }).listen()
      folder.addColor(this.skyParameters, 'sunColor').onChange(() => {
        this.updateEnvironment()
      })
      folder.add(this.skyParameters, 'inclination', 0, 0.5, 0.0001).onChange(() => {
        this.updateEnvironment()
      })
      folder.add(this.skyParameters, 'azimuth', 0, 1, 0.0001).onChange(() => {
        this.updateEnvironment()
      })
      folder.add(this.skyParameters, 'setDay')
      folder.add(this.skyParameters, 'setEvening')
      folder.add(this.skyParameters, 'setNight')
      folder.open()
    }

    this.updateEnvironment()
  }

  initEnvironment() {
    super.initEnvironment()

    if (this.config.models.roomBackground) {
      if (this.config.models.roomBackground.type === 'panorama') {
        this.initBackgroundPanorama(this.config.models.roomBackground)
      } else if (this.config.models.roomBackground.type === 'sky') {
        this.initBackgroundSky()
      }
    }

    if (this.config.environment) {
      this.switchEnvironmentScheme(this.config.environment, false)
    }
  }

  switchEnvironmentScheme(scheme, animate = true) {
    if (!scheme) {
      window.Logger.error('Scheme is required')
      throw new Error('Scheme is required')
    }
    if (!this.config.environmentPresets || !this.config.environmentPresets[scheme]) {
      window.Logger.error('Scheme not found', scheme, this.config.environmentPresets)
      throw new Error('Scheme not found')
    }

    if (animate) {
      this.animateEnvironmentTo(this.config.environmentPresets[scheme])
    } else {
      this.setEnvironment(this.config.environmentPresets[scheme])
    }
  }

  setEnvironment(params) {
    if (this.config.models.roomBackground.type === 'sky') {
      if (this.environmentTween) {
        this.environmentTween.stop()
        TWEEN.remove(this.environmentTween)
      }

      if (!this.skyParameters) {
        window.Logger.error('Environment should be initialised before setEnvironment call')
        throw new Error('Environment should be initialised before setEnvironment call')
      }

      if (typeof params.inclination !== 'undefined') {
        this.skyParameters.inclination = params.inclination
      }
      if (typeof params.azimuth !== 'undefined') {
        this.skyParameters.azimuth = params.azimuth
      }
      if (typeof params.ambientIntensity !== 'undefined') {
        let ambientLight = this.scene.getObjectByName('ambient')
        ambientLight.intensity = params.ambientIntensity
      }
      if (typeof params.sunColor !== 'undefined') {
        this.skyParameters.sunColor = params.sunColor
      }
      if (typeof params.ambientColor !== 'undefined') {
        this.skyParameters.ambientColor = params.ambientColor
      }

      this.updateEnvironment()
      this.requestCubeCamsUpdate()
    }
  }

  animateEnvironmentTo(params) {
    if (this.config.models.roomBackground.type === 'sky') {
      if (this.environmentTween) {
        this.environmentTween.stop()
        TWEEN.remove(this.environmentTween)
      }

      if (!params.time) {
        params.time = 3000
      }

      let ambientLight = this.scene.getObjectByName('ambient')
      let animStartParams = {}

      animStartParams.disable = typeof this.skyParameters.disable !== 'undefined' ? this.skyParameters.disable : false
      animStartParams.inclination = this.skyParameters.inclination
      animStartParams.azimuth = this.skyParameters.azimuth
      animStartParams.sunColor = this.skyParameters.sunColor
      animStartParams.ambientColor = ambientLight.color.getHex()
      animStartParams.ambientIntensity = ambientLight.intensity

      this.environmentTween = new TWEEN.Tween({ value: 0 })
      this.environmentTween.to({ value: 1 }, params.time)

      let targetSunColor = null
      if (typeof params.sunColor !== 'undefined') {
        targetSunColor = new THREE.Color(params.sunColor)
      }
      let targetAmbientColor = null
      if (typeof params.ambientColor !== 'undefined') {
        targetAmbientColor = new THREE.Color(params.ambientColor)
      }

      this.environmentTween.onUpdate((data) => {
        if (typeof params.inclination !== 'undefined') {
          this.skyParameters.inclination = THREE.Math.lerp(animStartParams.inclination, params.inclination, data.value)
        }
        if (typeof params.azimuth !== 'undefined') {
          this.skyParameters.azimuth = THREE.Math.lerp(animStartParams.azimuth, params.azimuth, data.value)
        }
        if (typeof params.ambientIntensity !== 'undefined') {
          ambientLight.intensity = THREE.Math.lerp(animStartParams.ambientIntensity, params.ambientIntensity, data.value)
        }
        if (targetSunColor) {
          let sColor = new THREE.Color(animStartParams.sunColor)
          this.skyParameters.sunColor = sColor.lerp(targetSunColor, data.value).getHex() // update THREE and use lerpHSL
        }
        if (targetAmbientColor) {
          let sColor = new THREE.Color(animStartParams.ambientColor)
          this.skyParameters.ambientColor = sColor.lerp(targetAmbientColor, data.value).getHex() // update THREE and use lerpHSL
        }

        this.updateEnvironment()
      })

      this.environmentTween.onComplete(() => {
        this.requestCubeCamsUpdate()
      })

      this.environmentTween.start()
    }
  }

  updateEnvironment() {
    if (this.config.models.roomBackground.type === 'sky') {
      let light = this.scene.getObjectByName('sunLight')
      let skyDome = this.scene.getObjectByName('skyDome')
      let sunSphere = this.scene.getObjectByName('sunSphere')

      if (!light || !skyDome) {
        window.Logger.error('Environment updated failed: sky objects not found')
        throw new Error('Environment updated failed: sky objects not found')
      }

      let theta = Math.PI * (this.skyParameters.inclination - 0.5)
      let phi = 2 * Math.PI * (this.skyParameters.azimuth - 0.5)
      sunSphere.position.x = this.skyParameters.distance * Math.cos(phi)
      sunSphere.position.y = this.skyParameters.distance * Math.sin(phi) * Math.sin(theta)
      sunSphere.position.z = this.skyParameters.distance * Math.sin(phi) * Math.cos(theta)

      light.visible = this.skyParameters.inclination <= 0.5
      light.color = new THREE.Color(this.skyParameters.sunColor)
      light.position.copy(sunSphere.position)

      skyDome.material.uniforms.sunPosition.value.copy(sunSphere.position)
    }

    this.renderer.shadowMap.needsUpdate = true
  }

  getMovementCursorTexture() {
    let texture = new THREE.Texture()
    let canvas
    let context
    let size = 128
    let border = 0.1
    let radius = size / 2 * (1 - border)

    canvas = document.createElement('canvas')
    context = canvas.getContext('2d')
    canvas.height = size
    canvas.width = size

    let centerX = size / 2
    let centerY = size / 2

    context.beginPath()
    context.arc(centerX, centerY, radius, 0, 2 * Math.PI, false)
    context.fillStyle = 'rgba(255,255,255,0.4)'
    context.fill()
    context.lineWidth = size * border
    context.strokeStyle = 'rgba(255,255,255,0.4)'
    context.stroke()

    texture.image = canvas
    texture.needsUpdate = true

    return texture
  }

  onModelObjectLoadProgress(xhr, data = null) {
    // window.Logger.log('MODEL OBJECT PROGRESS', (data ? data.id + ' ' + data.name : null))
    // if (xhr.lengthComputable) {
    //   window.Logger.log(Math.round(xhr.loaded / xhr.total * 100) + '% is loaded.')
    // }
  }

  onModelObjectLoadError(error, data = null) {
    if (data) {
      window.Logger.error('DATA: ', data)
    }

    throw new Error('MODEL OBJECT ERROR: ' + error)
  }

  onPointerScroll(delta) {
    if (this.moveByClickTween) {
      // block move by scroll while moving by click or exhibit
      return
    }

    this.onUserInteraction()

    if (delta < 0) {
      // move forward
      if (this.moveDirection <= 0) {
        this.moveSpeed = 0
        this.moveDirection = 1
      }
    } else if (delta > 0) {
      // move back
      if (this.moveDirection >= 0) {
        this.moveSpeed = 0
        this.moveDirection = -1
      }
    }

    this.moveSpeed += window.Config.options.movement.scrollMoveSpeed
    if (this.moveSpeed > window.Config.options.movement.scrollMaxMoveSpeed) {
      this.moveSpeed = window.Config.options.movement.scrollMaxMoveSpeed
    }
    this.unprocessedMove = true
  }

  /**
   * @return {{outside:boolean,correctedPosition:THREE.Vector3}}
   */
  checkCollision(thisPosition, nextPosition) {
    let result = {
      failed: false,
      outside: false,
      correctedPosition: nextPosition.clone(),
      intersectionPosition: nextPosition.clone()
    }

    if (window.Config.options.debug.ignoreCollisions) {
      return result
    }

    // push outside "solid" areas
    this.collisionPolyRestrictIn.map(poly => {
      let check = this.checkCollisionRestrict(nextPosition, poly, false)
      if (check.failed) {
        result = check

        let intersection = this.getPolygonIntersection(poly, thisPosition, nextPosition)
        if (intersection) {
          result.intersectionPosition.x = intersection.x
          result.intersectionPosition.z = intersection.y
        }
      }
    })

    // push inside "allowed" area
    let check = this.checkCollisionRestrict(nextPosition, this.collisionPolyRestrictOut, true)
    if (check.failed) {
      result = check

      if (thisPosition.distanceTo(nextPosition)) {
        let intersection = this.getPolygonIntersection(this.collisionPolyRestrictOut, thisPosition, nextPosition)
        if (intersection !== false) {
          result.intersectionPosition.x = intersection.x
          result.intersectionPosition.z = intersection.y
        }
      }
    }

    return result
  }

  /**
   * @return {{outside:boolean,correctedPosition:THREE.Vector3}}
   */
  checkCollisionRestrict(position, poly, restrictOut = true) {
    let result = {
      failed: false,
      outside: false,
      correctedPosition: position.clone(),
      intersectionPosition: position.clone()
    }

    let pointPos = classifyPoint(poly, [position.x, position.z])
    result.outside = pointPos > 0

    if (restrictOut === result.outside) {
      result.failed = true
      let minDist = Infinity
      poly.map((point, i) => {
        let nextPointIndex = (i + 1) % poly.length
        let nextPoint = poly[nextPointIndex]
        let line = new THREE.Line3(
          new THREE.Vector3(point[0], position.y, point[1]),
          new THREE.Vector3(nextPoint[0], position.y, nextPoint[1])
        )

        let closestPoint = new THREE.Vector3()
        line.closestPointToPoint(position, true, closestPoint)

        let dist = position.distanceTo(closestPoint)

        if (dist < minDist) {
          minDist = dist
          result.correctedPosition.copy(closestPoint)
        }
      })
    }

    return result
  }

  getPolygonIntersection(poly, p1, p2) {
    for (let i = 0; i < poly.length; i++) {
      let point = poly[i]
      let nextPointIndex = (i + 1) % poly.length
      let nextPoint = poly[nextPointIndex]

      let intersection = this.segmentIntersection(point[0], point[1], nextPoint[0], nextPoint[1], p1.x, p1.z, p2.x, p2.z)

      if (intersection) {
        return intersection
      }
    }

    return false
  }

  onPanoramaClick(event) {
    this.moveByClickRequest = true
  }

  sceneLoopUpdate(time, delta) {
    if (this.mixers.length > 0) {
      for (let i = 0; i < this.mixers.length; i++) {
        this.mixers[i].update(this.clock.getDelta())
      }
    }
    //
    // // auto move
    // if (this.unprocessedMove) {
    //   let collisionCheck = this.checkCollision(this.desiredPosition)
    //   if (collisionCheck.failed) {
    //     this.desiredPosition.copy(collisionCheck.correctedPosition)
    //   }
    //
    //   this.unprocessedMove = false
    // }

    if (this.controlsEnabled) {
      if (this.moveSpeed > 0) {
        let moveVector = new THREE.Vector3()
        // get current camera direction
        moveVector.setFromMatrixColumn(this.camera.matrix, 2) // Z column
        moveVector.y = 0// ignore vertical direction

        moveVector.normalize().multiplyScalar(-this.moveSpeed * this.moveDirection * delta)

        let thisPosition = this.camera.position.clone()
        let nextPosition = thisPosition.clone().add(moveVector)

        if (!thisPosition.equals(nextPosition)) { // if there is no need to move
          // collision check
          let collisionCheck = this.checkCollision(thisPosition, nextPosition)
          if (collisionCheck.failed) {
            // collision should additionally reduce speed
            this.moveSpeed -= window.Config.options.movement.moveSpeedDegradation * delta

            nextPosition.copy(collisionCheck.correctedPosition)
          }
        }

        this.camera.position.copy(nextPosition)

        this.moveSpeed -= window.Config.options.movement.moveSpeedDegradation * delta
        if (this.moveSpeed < 0) {
          this.moveSpeed = 0
        }

        this.isPointerMoved = true

        this.sendMovementDataEvent()
      }

      let autorotateActive = this.autoRotate.enabled && !this.autoRotate.paused
      if (this.floorPlane && (this.isPointerMoved || this.moveByClickRequest || autorotateActive)) {
        // update "move" cursor
        // this.mousePosition
        let mouse = new THREE.Vector2((this.mousePosition.x * 2) - 1, -(this.mousePosition.y * 2) + 1)
        this.interactiveRaycaster.setFromCamera(mouse, this.camera)
        let intersections = this.interactiveRaycaster.intersectObjects([this.floorPlane])

        if (intersections.length) {
          if (!this.movementCursor.visible) {
            this.viewportContainer.classList.add('viewport-interactive-hover')
          }

          this.movementCursor.visible = true
          this.movementCursor.position.copy(intersections[0].point)
          this.movementCursor.position.y = 1

          if (this.moveByClickRequest) {
            this.moveByClickRequest = false

            if (this.moveByClickTween) {
              this.stopMoveByTween()
            }

            this.moveSpeed = 0 // stop user movement

            this.movementCursor.visible = false
            let desiredPosition = intersections[0].point.clone()
            desiredPosition.y = this.camera.position.y

            let collisionCheck = this.checkCollision(this.camera.position.clone(), desiredPosition)
            if (collisionCheck.failed) {
              desiredPosition.copy(collisionCheck.correctedPosition)
            }

            this.unprocessedMove = true

            // disable controls and automove to destination point
            let moveDistance = this.camera.position.distanceTo(desiredPosition)
            if (moveDistance > 0) {
              let moveTime = moveDistance / window.Config.options.movement.clickMoveSpeed
              let tween = new TWEEN.Tween(this.camera.position)

              this.moveByClickTween = tween

              tween.easing(TWEEN.Easing.Cubic.InOut)
              tween.to(desiredPosition, moveTime * 1000)
              tween.onComplete(() => {
                TWEEN.remove(tween)
                this.moveByClickTween = false
                this.sendMovementDataEvent()
              })
              tween.start()
            }
          }
        } else {
          if (this.movementCursor.visible) {
            this.viewportContainer.classList.remove('viewport-interactive-hover')
          }

          this.moveByClickRequest = false
          this.movementCursor.visible = false
        }
      }

    }

    const limitCubeCamsUpdatePerFrame = 1
    let cubeCamerasUpdated = 0
    for (let i = 0; i < this.cubeCams.length; i++) {
      let cubeCameraPair = this.cubeCams[i]
      if (cubeCamerasUpdated >= limitCubeCamsUpdatePerFrame) {
        break
      }

      if (!cubeCameraPair.needsUpdate) {
        continue
      }

      if (cubeCameraPair.object) {
        cubeCameraPair.object.visible = false
      }

      cubeCameraPair.camera.update(this.renderer, this.scene)

      if (cubeCameraPair.object) {
        cubeCameraPair.object.visible = true

        // let materialPhongCube = new THREE.MeshPhongMaterial( { shininess: 50, color: 0xffffff, specular: 0x999999, envMap: cubeCameraPair.camera.renderTarget.texture } );
        // let sphere = new THREE.Mesh(new THREE.SphereBufferGeometry(50, 64, 32), materialPhongCube)
        // sphere.name = 'test sphere ' +  cubeCameraPair.object.name
        // sphere.position.set(250, 50, -700)
        // this.scene.add(sphere)
      }

      cubeCameraPair.needsUpdate = false

      cubeCamerasUpdated++
    }

    super.sceneLoopUpdate(time, delta)
  }

  gatherMovementData(data) {
    super.gatherMovementData(data)

    data.x = this.camera.position.x
    data.y = this.camera.position.z
  }

  onSceneIsLoaded() {
    super.onSceneIsLoaded()

    this.requestCubeCamsUpdate()
  }

  requestCubeCamsUpdate() {
    if (this.cubeCams) {
      this.cubeCams.forEach(cubeCameraPair => cubeCameraPair.needsUpdate = true)
    }
  }

  stopMoveByTween() {
    this.moveByClickTween.stop()
    TWEEN.remove(this.moveByClickTween)
    this.moveByClickTween = false
  }

  // @author: https://gist.github.com/gordonwoodhull/50eb65d2f048789f9558
  /**
   *
   * @param x1
   * @param y1
   * @param x2
   * @param y2
   * @param x3
   * @param y3
   * @param x4
   * @param y4
   * @return {boolean|Vector2}
   */
  segmentIntersection(x1, y1, x2, y2, x3, y3, x4, y4) {
    // window.segmentIntersection = this.segmentIntersection;
    const eps = 0.0000001

    function between(a, b, c) {
      return a - eps <= b && b <= c + eps
    }

    let x = ((x1 * y2 - y1 * x2) * (x3 - x4) - (x1 - x2) * (x3 * y4 - y3 * x4)) /
      ((x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4))
    let y = ((x1 * y2 - y1 * x2) * (y3 - y4) - (y1 - y2) * (x3 * y4 - y3 * x4)) /
      ((x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4))
    if (isNaN(x) || isNaN(y)) {
      return false
    } else {
      if (x1 >= x2) {
        if (!between(x2, x, x1)) {
          return false
        }
      } else {
        if (!between(x1, x, x2)) {
          return false
        }
      }
      if (y1 >= y2) {
        if (!between(y2, y, y1)) {
          return false
        }
      } else {
        if (!between(y1, y, y2)) {
          return false
        }
      }
      if (x3 >= x4) {
        if (!between(x4, x, x3)) {
          return false
        }
      } else {
        if (!between(x3, x, x4)) {
          return false
        }
      }
      if (y3 >= y4) {
        if (!between(y4, y, y3)) {
          return false
        }
      } else {
        if (!between(y3, y, y4)) {
          return false
        }
      }
    }

    return new THREE.Vector2(x, y)
  }
}

export default Scene3D
